/**
 * PeriodSelector — month/year picker for the Eva Company Detail page (SF-3834).
 *
 * A controlled antd <Select> whose options are the selectable "YYYY-MM" periods
 * (May 2026 → current month, newest first) from {@link monthOptions}. The value
 * stays a machine "YYYY-MM" string while each option is labelled human-friendly
 * (e.g. "June 2026") via a UTC-formatted Date. The min (May 2026) / max (current
 * month) bounds are enforced purely by the option list, so no extra range guard
 * is needed here.
 */
import { Select } from 'antd';
import { monthOptions } from '../../../utils/date';

export interface PeriodSelectorProps {
  /** The currently-selected period as "YYYY-MM". */
  value: string;
  /** Called with the newly-selected "YYYY-MM" period. */
  onChange: (period: string) => void;
  /** Whether the control is disabled (e.g. while access is resolving). */
  disabled?: boolean;
}

/** Human-friendly label for a "YYYY-MM" period, e.g. "June 2026" (UTC). */
function formatPeriodLabel(period: string): string {
  const d = new Date(`${period}-01T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return period;
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** Controlled month/year period picker for the Eva company-detail view. */
export const PeriodSelector: React.FC<PeriodSelectorProps> = ({ value, onChange, disabled }) => {
  const options = monthOptions().map((period) => ({
    value: period,
    label: formatPeriodLabel(period),
  }));
  return (
    <Select
      aria-label="Usage period"
      value={value}
      onChange={onChange}
      disabled={disabled}
      options={options}
      style={{ minWidth: 180 }}
    />
  );
};
